import '../../styles/class/aiCare.css';
import { Helmet } from 'react-helmet';
import { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import QueryString from 'qs';
import useAxios from '../../hooks/useAxios';
import { getAlexLearningData, getLatestCounselData, getReadiLearningData, getStudentInfoWithAbsense, getExamHistoryData } from '../../api/aiCare.api';
import { IGetStudentInfoWithAbsence, IAbsenceRecord, IGetReadiLearningRecords, IGetAlexLearningRecords, IGetLatestCounsel, IExamRecord, ISemesterExamData, IGetExamHistory } from '../../models/aiCare.model';
import Loader from '../../components/Loader';
import ErrorResponse from '../../components/ErrorResponse';
import useNotification from '../../hooks/useNotification';
import { addHyphen} from '../../lib/util';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducer';
import { getJwtToken } from '../../api/auth.api';
import { IGetJwtToken } from '../../models/auth.model';
import { 
    ChevronDown, ChevronUp, AlertCircle, FileText, X, 
    CheckCircle, Calendar, BookOpen, User, Phone, 
    School, GraduationCap, Layout,
    ArrowRight,
    TrendingUp,
    ArrowUp
  } from 'lucide-react';
import Analysis from '../../components/student/student/care/Analysis';
import Modal from '../../components/student/student/care/Modals';
import FixedPopover from '../../components/student/student/care/FixedPopover';
import Spinner from '../../components/Spinner';

/* MT3 보는 시험 레벨 */
export const MT3_LEVEL = ['Penta', 'Hexa'];

/* 학기 순서로 월 정렬 (3,4,5 -> 6,7,8 -> 9,10,11 -> 12,1,2) */
const SEMESTER_MONTH_ORDER: Record<number, number> = {
    3: 1, 4: 2, 5: 3,      // 봄학기
    6: 4, 7: 5, 8: 6,      // 여름학기
    9: 7, 10: 8, 11: 9,    // 가을학기
    12: 10, 1: 11, 2: 12   // 겨울학기
};

// Navigation Items
const NAV_ITEMS = [
    { label: '학습 데이터', id: 'section-learning', accKey: undefined, icon: BookOpen },
    { label: '성적 분석', id: 'section-analysis', accKey: undefined, icon: Layout },
    { label: '스킬 유형 분석', id: 'section-skill', accKey: 'skill', icon: ArrowRight },
    { label: '학습 종합 진단', id: 'section-diagnosis', accKey: 'diagnosis', icon: CheckCircle },
    { label: 'AI 총평', id: 'section-summary', accKey: 'summary', icon: FileText },
    { label: '다음 레벨 정보', id: 'section-nextLevel', accKey: 'nextLevel', icon: TrendingUp },
];

interface PopupData {
    type: 'NOTES' | 'WRONG_ANSWER' | 'TEST_DETAIL' | null; 
    data?: any;
}

// READI 데이터 인터페이스 (UI용)
interface ReadiMonthData {
    id: number;
    month: string;                    // '3월', '4월', '5월', '합계'
    totalCompleteCount: number;
    totalCount: number;
    totalActivity: string;            // '12/12', '9/12', '32/36'
    completionRate: number;           // 100.0, 75.0, 88.8
    elearningCompleteCount: number;
    elearningCount: number;
    eLearning: string;                // '8/8', '5/8', '20/24'
    icoachingCompleteCount: number;
    icoachingCount: number;
    iCoaching: string;                // '4/4', '3/4', '11/12'
    clinicCompleteCount: number;
    clinicCount: number;
    clinic: string;                   // '-', '1/1'
    clinicScore?: number;             // 60점 미만이면 경고 표시용
    hasClinicWarning?: boolean;       // 경고 아이콘 표시 여부
    isTotal?: boolean;                // 합계 행 여부
    incompleteUnits?: Array<{   // 미완료 활동 (드롭다운용)
        type: '미수행' | '점수 저조';
        unitName: string;
        title: string;
        date: string;
        score?: number;
    }>;
}

interface AlexMonthData {
    id: number;
    month: string;
    fictionCount: number;
    nonfictionCount: number;
    bookCount: number;
    overallAvgBookCount: number;
    totalWords: number;
    totalReadingTime: number;
    avgBookLevel: number;
    isTotal?: boolean;
}

const StudentCare = () => {
    const { auth } = useSelector((state: RootState) => state.auth);
    const [activePopup, setActivePopup] = useState<PopupData>({ type: null });
    const location = useLocation();
    const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });
    const memberCode = query.memberCode as string;
    const [isAbsenceOpen, setIsAbsenceOpen] = useState(false);
    const [elearningState, setElearningState] = useState<{ idx: number; anchor: HTMLElement } | null>(null);
    const [monthlyReadiData, setMonthlyReadiData] = useState<ReadiMonthData[]>([]);
    const [monthlyAlexData, setMonthlyAlexData] = useState<AlexMonthData[]>([]);
    const [semesterExamData, setSemesterExamData] = useState<ISemesterExamData[]>([]);
    const [currentYearSemesterExamData, setCurrentYearSemesterExamData] = useState<ISemesterExamData[]>([]);
    const [alexLevelName, setAlexLevelName] = useState<string>('');
    const [isMT3Level, setIsMT3Level] = useState(false);
    const [jwtToken, setJwtToken] = useState<string | undefined>(undefined);
    const [isNextLevelOpen, setIsNextLevelOpen] = useState(false);

    //01. 학생정보 조회
    const { data: studentData, isLoading, error, execute } = useAxios<IGetStudentInfoWithAbsence>();
    //02. READI 데이터 조회
    const { 
        data: readiData, isLoading: readiLoading, error: readiError, 
        execute: executeReadi 
    } = useAxios<IGetReadiLearningRecords>();
    //03. Alex 데이터 조회
    const { 
        data: alexData, isLoading: alexLoading, error: alexError, 
        execute: executeAlex 
    } = useAxios<IGetAlexLearningRecords>();

    //04. 상담 데이터 조회
    const { 
        data: counselData, isLoading: counselLoading, error: counselError, 
        execute: executeCounsel 
    } = useAxios<IGetLatestCounsel>();

    //05. 시험 이력 조회
    const { 
        data: examData, isLoading: examLoading, error: examError, 
        execute: executeExam 
    } = useAxios<IGetExamHistory>();

    // JWT 토큰 조회
    const { data: jwtTokenData, execute: executeJwtToken } = useAxios<IGetJwtToken>();

    // JWT 토큰 받기
    useEffect(() => {
        if (memberCode && auth.memberCode) {
            executeJwtToken(getJwtToken({
                adminMemCode: auth.memberCode,
                studentMemCode: memberCode,
            }));
        }
    }, [memberCode, auth.memberCode]);

    // JWT 토큰 저장
    useEffect(() => {
        if (jwtTokenData?.data) {
            setJwtToken(jwtTokenData.data);
        }
    }, [jwtTokenData]);

    useEffect(() => {
        if (memberCode && jwtToken) {
            execute(getStudentInfoWithAbsense(memberCode, jwtToken));
            executeReadi(getReadiLearningData(memberCode, jwtToken));
            executeAlex(getAlexLearningData(memberCode, jwtToken));
            executeCounsel(getLatestCounselData(memberCode, jwtToken));
            executeExam(getExamHistoryData(memberCode, jwtToken));
        }
    }, [memberCode, jwtToken]);

    const { errorNotification } = useNotification();
    const processedErrorRef = useRef<string | null>(null);

    // 학생 정보 에러 처리 (detail 메시지 표시)
    useEffect(() => {
        if (error?.response?.status) {
            // 이미 처리한 에러인지 확인 (같은 에러 메시지면 한 번만 표시)
            const errorMessage = 
                error.response?.data?.detail ||
                error.response?.data?.data ||
                error.response?.data?.error ||
                '오류가 발생했습니다.';
            
            const errorKey = `${error.response.status}-${errorMessage}`;
            if (processedErrorRef.current !== errorKey) {
                processedErrorRef.current = errorKey;
                errorNotification(errorMessage);
            }
        } else {
            // 에러가 없으면 처리 기록 초기화
            processedErrorRef.current = null;
        }
    }, [error, errorNotification]);

    useEffect(() => {
        if (studentData) {
            setIsMT3Level(fnIsMT3Level(studentData.student_info.course_name));
        }
    }, [studentData]);

    useEffect(() => {
        if (readiData) {
            const monthlyData = readiData.learning_records.map((record) => ({
                id: 0,
                month: record.learning_month + '월',
                monthNum: parseInt(record.learning_month),
                totalCompleteCount: record.total_complete_count,
                totalCount: record.total_count,
                totalActivity: `${record.total_complete_count}/${record.total_count}`,
                completionRate: record.total_completion_rate,
                elearningCompleteCount: record.elearning_complete_count,
                elearningCount: record.elearning_count,
                eLearning: `${record.elearning_complete_count}/${record.elearning_count}`,
                icoachingCompleteCount: record.icoaching_complete_count,
                icoachingCount: record.icoaching_count,
                iCoaching: `${record.icoaching_complete_count}/${record.icoaching_count}`,
                clinicCompleteCount: record.clinic_complete_count,
                clinicCount: record.clinic_count,
                clinic: record.clinic_count > 0 ? `${record.clinic_complete_count}/${record.clinic_count}` : '-',
                clinicScore: record.clinic_avg_score,
                hasClinicWarning: record.is_low_score === 'Y' ? true : false,
                incompleteUnits: record.incomplete_units.map(units => ({
                    type: (units.total_result_score === null ? '미수행' : '점수 저조') as '미수행' | '점수 저조',
                    unitName: (units.parent_curriculum_name !== null ? units.parent_curriculum_name + ' ' : '') + units.last_curriculum_name ,
                    title: units.display_name || units.curriculum_name,
                    date: units.plan_date,
                    score: units.total_result_score > 0 ? units.total_result_score : undefined
                }))
            }));

            // 학기 순서로 정렬
            monthlyData.sort((a, b) => {
                return (SEMESTER_MONTH_ORDER[a.monthNum] || 0) - (SEMESTER_MONTH_ORDER[b.monthNum] || 0);
            });

            // 정렬 후 id 할당
            monthlyData.forEach((item, index) => {
                item.id = index;
            });

            // 합계 행 추가
            const totals = calculateReadiTotals(monthlyData);
            setMonthlyReadiData([...monthlyData, totals]);
        }
    }, [readiData]);

    useEffect(() => {
        if (alexData) {
            const monthlyData = alexData.learning_records.map((record, index) => ({
                id: index,
                month: record.learning_month + '월',
                monthNum: parseInt(record.learning_month),
                fictionCount: record.fiction_count,
                nonfictionCount: record.nonfiction_count,
                bookCount: record.book_count,
                overallAvgBookCount: record.overall_avg_book_count,
                totalWords: record.total_words,
                totalReadingTime: record.total_reading_time,
                avgBookLevel: record.avg_book_level,
                isTotal: false,
            }));

            // 학기 순서로 정렬
            monthlyData.sort((a, b) => {
                return (SEMESTER_MONTH_ORDER[a.monthNum] || 0) - (SEMESTER_MONTH_ORDER[b.monthNum] || 0);
            });

            setAlexLevelName(alexData.level_name);

            const totals = calculateAlexTotals(monthlyData);
            setMonthlyAlexData([...monthlyData, totals]);
        }
    }, [alexData]);

    useEffect(() => {
        if (examData && examData.exams) {
            // 전체 데이터
            const transformedData = transformExamDataBySemester(examData.exams);
            setSemesterExamData(transformedData);

            // Test History: PT 포함, 전달받은 순서 기준으로 마지막 4개만 노출 (부족하면 좌측부터 placeholder로 채움)
            const latest = transformedData.slice(-4);

            const missing = Math.max(0, 4 - latest.length);
            const placeholders: ISemesterExamData[] = Array.from({ length: missing }).map(() => ({
                semesterName: '',
                semesterDisplayName: '',
                levelName: '',
                isPlacement: false,
                isPlaceholder: true,
                scores: { MT1: '-', MT2: '-', TT: '-' },
                courseName: '',
                assessLevel: { MT1: '', MT2: '', TT: '' },
            }));

            setCurrentYearSemesterExamData([...placeholders, ...latest]);
        }
    }, [examData]);

    const studentInfo = studentData?.student_info;
    const absenceRecords = studentData?.absence_records || [];

    const closePopup = () => setActivePopup({ type: null });

    const [accordions, setAccordions] = useState({
        chart: true,
        skill: false,
        diagnosis: false,
        summary: false,
        nextLevel: false,
        counseling: false
    });

    const PopoverLayer = ({ children, onClose }: { children?: React.ReactNode; onClose: () => void }) => {
        const ref = useRef<HTMLDivElement>(null);
        useEffect(() => {
            const handleClickOutside = (event: MouseEvent) => {
                if (ref.current && !ref.current.contains(event.target as Node)) {
                    onClose();
                }
            };
            document.addEventListener("mousedown", handleClickOutside);
            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
            };
        }, [onClose]);
    
        return (
            <div ref={ref} className="popover" style={{ top: '100%', left: 0, marginTop: '0.25rem', width: '18rem' }}>
                {children}
            </div>
        );
    };  
    
    const getReadiScoreColorClass = (score: number | string, isText: boolean = true) => {
        if (typeof score === 'string') return 'text-slate-400'; // For '-'
        const isHigh = score >= 80;
        if (isText) return isHigh ? '' : 'care-text-red';
        return isHigh ? '' : 'bg-red-500';
    };

    const getScoreColorClass = (score: number | string, assessLevel: string, isText: boolean = true) => {
        if (typeof score === 'string') return 'text-slate-400'; // For '-'
        if (assessLevel === 'Advanced') return 'care-text-blue';
        if (assessLevel === 'Developing') return 'care-text-red';
        if (assessLevel === 'On') return 'bg-slate-800';
        return 'bg-slate-800';
    };
    
    const careFormatDate = (dateString: string): string => {
        if (!dateString) return '';
        // 8자리 문자열을 yyyy-MM-dd로 변환
        if (dateString.length === 8) {
            const year = dateString.substring(0, 4);
            const month = dateString.substring(4, 6);
            const day = dateString.substring(6, 8);
            return `${year}-${month}-${day}`;
        }
        return dateString;
    };
    
    const calculateReadiTotals = (data: ReadiMonthData[]): ReadiMonthData => {
        const totalCompleteCount = data.reduce((acc, curr) => acc + curr.totalCompleteCount, 0);
        const totalCount = data.reduce((acc, curr) => acc + curr.totalCount, 0);
        const totalActivity = `${totalCompleteCount}/${totalCount}`;
        const completionRate = totalCount ? totalCompleteCount / totalCount * 100 : 0;
        const elearningCompleteCount = data.reduce((acc, curr) => acc + curr.elearningCompleteCount, 0);
        const elearningCount = data.reduce((acc, curr) => acc + curr.elearningCount, 0);
        const eLearning = `${elearningCompleteCount}/${elearningCount}`;
        const icoachingCompleteCount = data.reduce((acc, curr) => acc + curr.icoachingCompleteCount, 0);
        const icoachingCount = data.reduce((acc, curr) => acc + curr.icoachingCount, 0);
        const iCoaching = `${icoachingCompleteCount}/${icoachingCount}`;
        const clinicCompleteCount = data.reduce((acc, curr) => acc + curr.clinicCompleteCount, 0);
        const clinicCount = data.reduce((acc, curr) => acc + curr.clinicCount, 0);
        const clinic = `${clinicCompleteCount}/${clinicCount}`;
    
        return {
            id: data.length > 0 ? data[data.length - 1].id + 1 : 0,
            month: '합계',
            totalCompleteCount,
            totalCount,
            totalActivity,
            completionRate,
            elearningCompleteCount,
            elearningCount,
            eLearning,
            icoachingCompleteCount,
            icoachingCount,
            iCoaching,
            clinicCompleteCount,
            clinicCount,
            clinic,
            clinicScore: clinicCompleteCount > 0 ? data.reduce((acc, curr) => acc + (curr.clinicScore || 0), 0) / data.filter(d => d.clinicScore).length : 0,
            hasClinicWarning: false,
            isTotal: true,
        };
    }
    
    const calculateAlexTotals = (data: AlexMonthData[]): AlexMonthData => {
        const totalFictionCount = data.reduce((acc, curr) => acc + curr.fictionCount, 0);
        const totalNonfictionCount = data.reduce((acc, curr) => acc + curr.nonfictionCount, 0);
        const totalBookCount = data.reduce((acc, curr) => acc + curr.bookCount, 0);
        // overallAvgBookCount가 0이 아닌 값들만 필터링하여 평균 계산
        const validOverallAvgBookCounts = data.filter(item => item.overallAvgBookCount !== 0);
        /*
        const totalOverallAvgBookCount = validOverallAvgBookCounts.length > 0
            ? Math.round((validOverallAvgBookCounts.reduce((acc, curr) => acc + curr.overallAvgBookCount, 0) / validOverallAvgBookCounts.length) * 10) / 10
            : 0;
        */
        const totalOverallAvgBookCount = Math.round(data.reduce((acc, curr) => acc + curr.overallAvgBookCount, 0) * 10) / 10;
        const totalTotalWords = data.reduce((acc, curr) => acc + curr.totalWords, 0);
        const totalTotalReadingTime = data.reduce((acc, curr) => acc + curr.totalReadingTime, 0);
        // avgBookLevel이 0이 아닌 값들만 필터링하여 평균 계산
        const validAvgBookLevels = data.filter(item => item.avgBookLevel !== 0);
        const totalAvgBookLevel = validAvgBookLevels.length > 0
            ? Math.round((validAvgBookLevels.reduce((acc, curr) => acc + curr.avgBookLevel, 0) / validAvgBookLevels.length) * 10) / 10
            : 0;
    
        return {
            id: data.length > 0 ? data[data.length - 1].id + 1 : 0,
            month: '합계',
            fictionCount: totalFictionCount,
            nonfictionCount: totalNonfictionCount,
            bookCount: totalBookCount,
            overallAvgBookCount: totalOverallAvgBookCount,
            totalWords: totalTotalWords,
            totalReadingTime: Math.round(totalTotalReadingTime * 10) / 10,
            avgBookLevel: totalAvgBookLevel,
            isTotal: true,
        };
    }
    
    // MT3 레벨인지 확인하는 함수
    const fnIsMT3Level = (levelName: string | null | undefined): boolean => {
        if (!levelName) return false;
        return MT3_LEVEL.some(level => levelName.toLowerCase().includes(level.toLowerCase()));
    };
    
    // 시험 데이터를 학기별로 그룹화하는 함수
    const transformExamDataBySemester = (exams: IExamRecord[]): ISemesterExamData[] => {
        const result: ISemesterExamData[] = [];
        
        // 학기명을 표시용으로 변환 (예: "2025 SPRING" -> "25 Spring")
        /*
        const formatSemesterDisplay = (semesterName: string): string => {
            if (!semesterName) return '';
            const parts = semesterName.split(' ');
            if (parts.length < 2) return semesterName;
            const year = parts[0].slice(-2); // "2025" -> "25"
            const season = parts[1].charAt(0) + parts[1].slice(1).toLowerCase(); // "SPRING" -> "Spring"
            return `${year} ${season}`;
        };
        */
    
        // exam_name에서 MT 번호 추출 (예: "2025 봄학기 Hexa 2 MT1" -> "MT1")
        const extractMTNumber = (examName: string): string | null => {
            const match = examName.match(/MT(\d+)/i);
            return match ? `MT${match[1]}` : null;
        };
    
        // exam_name에서 Type 추출 (예: "Placement Test Type 1" -> "Type 1")
        const extractPlacementType = (examName: string): string => {
            const match = examName.match(/Type\s*(\d+)/i);
            return match ? `Type ${match[1]}` : '';
        };
    
        // Placement Test 처리
        const placementTests = exams.filter(e => e.exam_type_code === 'PT' && e.result_score !== null);
        placementTests.forEach(pt => {
            result.push({
                semesterName: '',
                semesterDisplayName: '',
                levelName: 'Placement Test',
                isPlacement: true,
                placementType: extractPlacementType(pt.exam_name),
                placementResult: `${pt.result_score}점`,
                scores: { MT1: '-', MT2: '-', TT: '-' },
                courseName: pt.course_name,
                assessLevel: { MT1: '', MT2: '', TT: '' },
            });
        });
    
        // 학기별로 그룹화 (학기 정렬 키 추가)
        const semesterMap = new Map<string, { courseName: string; sortKey: string; exams: IExamRecord[] }>();
        
        exams.filter(e => e.exam_type_code !== 'PT' && e.semester_name).forEach(exam => {
            const key = exam.semester_name;
            if (!semesterMap.has(key)) {
                // learning_year_code + semester_div로 정렬 키 생성
                const sortKey = (exam.learning_year_code || '') + (exam.semester_div || '');
                semesterMap.set(key, { courseName: exam.course_name || '', sortKey, exams: [] });
            }
            semesterMap.get(key)!.exams.push(exam);
        });
    
        // 받은 순서대로 사용
        const semesters = Array.from(semesterMap.entries());

        /*
        // sortKey로 정렬 (년도 + semesterDiv 순서)
        const sortedSemesters = Array.from(semesterMap.entries()).sort((a, b) => {
            return a[1].sortKey.localeCompare(b[1].sortKey);
        });  
        */
        
        // 각 학기별 데이터 생성
        semesters.forEach(([semesterName, data]) => {
            // 각 학기의 마지막 exam의 level_name 사용
            const lastExam = data.exams[data.exams.length - 1];
            const levelName = lastExam?.level_name;
            const courseName = lastExam?.course_name || data.courseName;
            const hasMT3 = fnIsMT3Level(courseName);
            
            const scores: { MT1: number | string; MT2: number | string; MT3?: number | string; TT?: number | string } = {
                MT1: '-',
                MT2: '-',
                MT3: hasMT3 ? '-' : undefined,
                TT: hasMT3 ? undefined : '-'
            };
            const assessLevel: { MT1: string; MT2: string; MT3?: string; TT?: string } = { MT1: '', MT2: '', MT3: '', TT: '' };
    
            data.exams.forEach(exam => {
                if (exam.exam_type_code === 'MT') {
                    const mtNum = extractMTNumber(exam.exam_name);
                    if (mtNum === 'MT1') { scores.MT1 = exam.result_score; assessLevel.MT1 = exam.assess_level; }
                    else if (mtNum === 'MT2') {scores.MT2 = exam.result_score; assessLevel.MT2 = exam.assess_level; }
                    else if (mtNum === 'MT3' && hasMT3) { scores.MT3 = exam.result_score; assessLevel.MT3 = exam.assess_level; }
                } else if (exam.exam_type_code === 'LT' && !hasMT3) {
                    {scores.TT = exam.result_score; assessLevel.TT = exam.assess_level; }
                }
            });
    
            result.push({
                semesterName: semesterName,
                semesterDisplayName: semesterName,
                levelName: levelName, // 각 학기의 마지막 exam의 레벨 사용
                isPlacement: false,
                scores,
                courseName: courseName,
                assessLevel: assessLevel,
            });
        });
    
        return result;
    }

    const handleNavClick = (targetId: string, accordionKey?: keyof typeof accordions) => {
        if (accordionKey) {
          setAccordions(prev => ({ ...prev, [accordionKey]: true }));
        }
        
        // Allow time for accordion to expand
        setTimeout(() => {
            const element = document.getElementById(targetId);
            if (element) {
                const headerOffset = 80; // Sticky header + padding
                const elementPosition = element.getBoundingClientRect().top;
                const offsetPosition = elementPosition + window.scrollY - headerOffset;
                
                window.scrollTo({
                top: offsetPosition,
                behavior: "smooth"
                });
            }
        }, accordionKey ? 100 : 0);
    };

    const handleScrollTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <>
            <Helmet>
                <title>CANB AI CARE</title>
            </Helmet>
            {isLoading && <Loader />}
            <ErrorResponse error={readiError || alexError || counselError || examError} />
            
            <div className="app-layout care-root-layout">
                      {/* --- Side Navigation --- */}
                <div className="side-nav-container">
                    {/* Nav Items Group */}
                    <div className="nav-group">
                    {NAV_ITEMS.map(item => {
                        // nextLevel 항목은 isNextLevelOpen이 true일 때만 표시
                        if (item.accKey === 'nextLevel' && !isNextLevelOpen) {
                            return null;
                        }
                        return (
                            <button 
                            key={item.label}
                            onClick={() => handleNavClick(item.id, item.accKey as keyof typeof accordions)}
                            title={item.label}
                            className="nav-item group"
                            >
                            <item.icon size={20} className="nav-item-icon" />
                            <span className="nav-item-label">{item.label}</span>
                            </button>
                        );
                    })}
                    </div>
                    
                    {/* Top Button */}
                    <button 
                    onClick={handleScrollTop}
                    className="nav-top-btn"
                    title="Top"
                    >
                    <ArrowUp size={20} />
                    </button>
                </div>

                 {/* --- Header --- */}
                <header className="app-header">
                    <div className="header-inner">
                    <div className="flex items-center gap-2">
                        <h1 className="logo-text">CANB AI CARE <span className="logo-sub">| 학생 Dashboard</span></h1>
                    </div>
                    <div className="flex items-center gap-4 text-white">
                        <span className="care-hidden md-inline">{studentInfo?.teacher_name ? '담임 : ' + studentInfo?.teacher_name : ''}</span>
                        <div className="care-avatar">
                        {< User size={18} color="white" />}
                        </div>
                    </div>
                    </div>
                </header>

                <main className="main-container">
                    {/* --- 1. Student Information --- */}
                    {studentInfo && (
                        <section className="student-section animate-fadeIn">
                            <div className="flex items-center gap-4">
                                <div className="flex flex-col">
                                <h2 className="student-name">
                                    {studentInfo.member_name} ({studentInfo.member_name_eng})
                                    <span className="badge badge-indigo">{studentInfo.course_name}</span>
                                </h2>
                                <div className="info-list">
                                    <span className="flex items-center gap-1">
                                        <GraduationCap size={16}/>
                                        {studentInfo.grade}
                                    </span>
                                    <span className="dot-separator"></span>
                                    <span className="flex items-center gap-1">
                                        <School size={16}/>
                                        {studentInfo.school_name}
                                    </span>
                                    <span className="dot-separator"></span>
                                    <span className="flex items-center gap-1">
                                        <Phone size={16}/>
                                        {addHyphen(studentInfo.parent_mobile)}
                                    </span>
                                </div>
                                </div>
                            </div>

                            <div className="flex items-center gap-4">
                                <div className="relative">
                                    <div className="flex items-center gap-2 bg-slate-50 rounded care-absence-box">
                                        <span className="font-medium text-slate-600">결석</span>
                                        <div className="flex items-center gap-1">
                                        <span className="font-bold text-red care-text-lg"> {absenceRecords.length}</span>
                                        <button 
                                            onClick={() => setIsAbsenceOpen(!isAbsenceOpen)}
                                            className="care-btn-icon"
                                        >
                                            <ChevronDown size={18} className="text-slate-400" style={{ transform: isAbsenceOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                                        </button>
                                        </div>
                                    </div>
                                    {isAbsenceOpen && (
                                        <PopoverLayer onClose={() => setIsAbsenceOpen(false)}>
                                        <div className="care-p-1">
                                            <h4 className="flex items-center gap-2 care-section-header">
                                            <Calendar size={14} /> 결석 날짜 리스트
                                            </h4>
                                            <ul className="care-list-unstyled">
                                            {absenceRecords.map((item, i) => (
                                                <li key={i} className="bg-slate-50 care-list-item">
                                                <span className="font-semibold text-slate-600">{careFormatDate(item.absence_date)}</span>
                                                <span className="care-badge-small">{item.attend_status_name}</span>
                                                </li>
                                            ))}
                                            </ul>
                                        </div>
                                        </PopoverLayer>
                                    )}
                                </div>
                                <button onClick={() => setActivePopup({ type: 'NOTES' })} className="btn btn-primary-care">
                                    <FileText size={18} />
                                    <span>특이사항, 상담</span>
                                </button>
                            </div>
                        </section>
                    )}

                    {/* --- 2. Learning Metrics --- */}
                    <div id="section-learning" className="grid-cols-12">
                        {/* READi */}
                        <section className="col-span-6 care-card">
                            <div className="card-header-dark">
                            <div className="card-title-row">
                                <BookOpen size={22} color="white" />
                                <h2 className="card-title text-white">READI</h2>
                            </div>
                            </div>
                            <div className="table-container">
                                <table className="care-table-custom">
                                <thead>
                                    <tr>
                                    <th></th>
                                    <th>Total</th>
                                    <th>Completion(%)</th>
                                    <th>e-Learning</th>
                                    <th>iCoaching</th>
                                    <th>Clinic</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {monthlyReadiData.map((row, i) => (
                                    <tr key={i} className={row.isTotal ? 'care-table-row-total' : ''}>
                                        <td className="text-center">{row.month}</td>
                                        <td className="text-slate-600">{row.totalActivity}</td>
                                        <td className={'font-semibold ' +  getReadiScoreColorClass(row.completionRate)}>
                                        {row.completionRate.toFixed(1)}%
                                        </td>
                                        <td>
                                        <div className="flex items-center gap-1">
                                            <span>{row.eLearning}</span>
                                            {!row.isTotal && (
                                            <button 
                                                onClick={(e) => { 
                                                e.stopPropagation(); 
                                                if (elearningState?.idx === i) {
                                                    setElearningState(null);
                                                } else {
                                                    setElearningState({ idx: i, anchor: e.currentTarget });
                                                }
                                                }}
                                                className="care-btn-icon"
                                                style={{ padding: '0.125rem', borderRadius: '0.25rem', background: elearningState?.idx === i ? 'var(--slate-200)' : 'transparent' }}
                                            >
                                                <ChevronDown size={16} className="text-slate-400" />
                                            </button>
                                            )}
                                        </div>
                                        </td>
                                        <td className="text-slate-600">{row.iCoaching}</td>
                                        <td className="text-slate-600">
                                        <div className="flex items-center gap-1">
                                            <span>{row.clinic}</span>
                                            {row.hasClinicWarning === true && (
                                            <AlertCircle size={16} className="text-red" />
                                            )}
                                        </div>
                                        </td>
                                    </tr>
                                    ))}
                                </tbody>
                                </table>
                            </div>
                            <div className="bg-slate-50 readi-notice">
                                <AlertCircle size={16} className="text-red" />
                                <span>Clinic : Checkup 60점 미만인 경우 경고 아이콘 표시</span>
                            </div>
                        </section>

                        {/* Alex */}
                        <section className="col-span-6 care-card">
                            <div className="card-header-dark">
                            <div className="card-title-row">
                                <Layout size={22} color="white" />
                                <h2 className="card-title text-white">Alex</h2>
                            </div>
                            <span className="care-level-badge">기준 레벨 : {alexLevelName}</span>
                            </div>
                            <div className="table-container" >
                                {alexLoading ? <div style={{ position: 'relative', height: '170px'}}><Spinner /></div> : null}
                                {alexLoading ? null : (
                                <table className="care-table-custom care-text-center">
                                <thead>
                                    <tr>
                                    <th rowSpan={2} className="care-text-center-border-right"></th>
                                    <th colSpan={4} className="care-text-center-border-right" style={{ borderBottom: '1px solid var(--slate-200)' }}>읽은 도서 수</th>
                                    <th rowSpan={2} className="care-text-center-border-right">읽은 단어 수</th>
                                    <th rowSpan={2} className="care-text-center-border-right">독서 시간</th>
                                    <th rowSpan={2} className="care-text-center">도서 레벨 평균</th>
                                    </tr>
                                    <tr>
                                    <th className="care-table-header-small">F</th>
                                    <th className="care-table-header-small">NF</th>
                                    <th className="care-table-header-small">합계</th>
                                    <th className="care-table-header-small">레벨 전체 평균</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {monthlyAlexData.map((row, i) => (
                                    <tr key={i} className={row.isTotal ? 'care-table-row-total' : ''}>
                                        <td className="care-border-right">{row.month}</td>
                                        <td className="care-border-right text-slate-600">{row.fictionCount}</td>
                                        <td className="care-border-right text-slate-600">{row.nonfictionCount}</td>
                                        <td className="care-border-right font-semibold">{row.bookCount}</td>
                                        <td className="care-border-right text-slate-500">{row.overallAvgBookCount}</td>
                                        <td className="care-border-right text-slate-600">{row.totalWords != null ? new Intl.NumberFormat('ko-KR').format(row.totalWords) : ''}</td>
                                        <td className="care-border-right text-slate-600">{row.totalReadingTime}분</td>
                                        <td className="text-slate-600">{row.avgBookLevel}</td>
                                    </tr>
                                    ))}
                                </tbody>
                                </table>
                                )}
                            </div>
                        </section>
                    </div>

                    {/* --- 3. Test History --- */}
                    <section className="care-card">
                        <div className="card-header-dark">
                        <div className="card-title-row">
                            <CheckCircle size={22} color="white" />
                            <h2 className="card-title text-white">Test History</h2>
                        </div>
                        <button onClick={() => setActivePopup({type: 'TEST_DETAIL'})} className="care-btn-secondary rounded-full">
                            Previous
                        </button>
                        </div>
                        
                        <div className="care-card-body grid-cols-2 grid-cols-5 care-grid-auto-fit">
                        {currentYearSemesterExamData.map((test, index) => (
                            <div key={index} className="care-card-base">
                            {!test.isPlaceholder && (
                                <div className="flex justify-between items-center care-mb-1">
                                    <h4 className="font-bold text-slate-800 care-m-0">{!test.isPlacement ? test.semesterDisplayName : ''}</h4>
                                    <span className="badge" style={{ fontSize: '1rem', lineHeight: '17.5px', fontWeight: 700, backgroundColor: test.isPlacement ? 'var(--amber-50)' : 'var(--indigo-50)', color: test.isPlacement ? '#b45309' : 'var(--indigo-600)' }}>
                                    {test.isPlacement ? test.levelName : test.courseName}
                                    </span>
                                </div>
                            )}
                            
                            {test.isPlaceholder ? (
                                <>
                                <div className="flex justify-between items-center care-mb-1">
                                    <h4 className="font-bold text-slate-800 care-m-0">&nbsp;</h4>
                                    <span className="badge" style={{ fontSize: '1rem', lineHeight: '17.5px', fontWeight: 700,  }}></span>
                                </div>
                                <table className="w-full test-history-table" >
                                <thead>
                                    <tr className="text-slate-400 care-text-sm">
                                    <th className="test-history-td">MT1</th>
                                    <th className="test-history-td">MT2</th>
                                    <th className="test-history-td">TT</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="font-bold text-slate-600">
                                    <td className="font-bold text-slate-600 test-history-td">{'-'}</td>
                                    <td className="font-bold text-slate-600 test-history-td">{'-'}</td>
                                    <td className="font-bold text-slate-600 test-history-td">{'-'}</td>
                                    </tr>
                                </tbody>
                                </table>
                                </>
                            ) : test.isPlacement ? (
                                <div className="text-center care-mt-1 care-flex-col-gap">
                                <div className="font-bold text-slate-600">{test.placementType}</div>
                                <div className="font-bold care-border-top">{test.placementResult + ' (' + test.courseName + ')'}</div>
                                </div>
                            ) : (
                                <table className="w-full test-history-table" >
                                <thead>
                                    <tr className="text-slate-400 care-text-sm">
                                    <th className="test-history-td">MT1</th>
                                    <th className="test-history-td">MT2</th>
                                    <th className="test-history-td">{MT3_LEVEL.some(level => test.courseName.toLowerCase().includes(level.toLowerCase())) ? 'MT3' : 'TT'}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="font-bold text-slate-600">
                                    <td className={'test-history-td ' + getScoreColorClass(test.scores?.MT1 || '-', test.assessLevel?.MT1 || '')}>{test.scores?.MT1 ? test.scores?.MT1 : '-'}</td>
                                    <td className={'test-history-td ' + getScoreColorClass(test.scores?.MT2 || '-', test.assessLevel?.MT2 || '')}>{test.scores?.MT2 ? test.scores?.MT2 : '-'}</td>
                                    <td className={'test-history-td ' + getScoreColorClass(MT3_LEVEL.some(level => test.courseName.toLowerCase().includes(level.toLowerCase())) ? (test.scores?.MT3 || '-') : (test.scores?.TT || '-'), MT3_LEVEL.some(level => test.courseName.toLowerCase().includes(level.toLowerCase())) ? (test.assessLevel?.MT3 || '') : (test.assessLevel?.TT || ''))}>
                                        {MT3_LEVEL.some(level => test.courseName.toLowerCase().includes(level.toLowerCase())) ? (test.scores?.MT3 ? test.scores?.MT3 : '-') : (test.scores?.TT ? test.scores?.TT : '-')}
                                    </td>
                                    </tr>
                                </tbody>
                                </table>
                            )}
                            </div>
                        ))}
                        </div>
                    </section>

                    {/* --- 4. Grade Analysis --- */}
                    {studentData && (
                        <Analysis 
                            memberCode={memberCode} 
                            isMT3Level={isMT3Level} 
                            accordions={accordions} 
                            setAccordions={setAccordions} 
                            studentInfo={studentData.student_info}
                            jwtToken={jwtToken}
                            setIsNextLevelOpen={setIsNextLevelOpen}
                        />
                    )}
                    
                    {/* --- Popups --- */}
                    <Modal isOpen={activePopup.type === 'NOTES'} onClose={closePopup} title="특이사항 및 상담 내역">
                        <div className="flex flex-col gap-8">
                            {/* 0. Memo (New) */}
                            <div>
                            <h3 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
                                <span style={{ width: '0.375rem', height: '1.5rem', backgroundColor: 'var(--slate-500)', borderRadius: '0.125rem' }}></span>
                                메모
                            </h3>
                            <div style={{ backgroundColor: 'var(--amber-50)', border: '1px solid var(--amber-200)', borderRadius: '0.5rem', padding: '1rem', color: 'var(--slate-700)', lineHeight: 1.625 }}>
                                {counselData?.latest_record?.memo === null ? '-' : counselData?.latest_record?.memo}
                            </div>
                            </div>

                            {/* 1. Recent Special Notes */}
                            <div>
                            <h3 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
                                <span style={{ width: '0.375rem', height: '1.5rem', backgroundColor: 'var(--blue-600)', borderRadius: '0.125rem' }}></span>
                                최근 특이사항
                            </h3>
                            <div style={{ overflowX: 'auto', borderRadius: '0.5rem', border: '1px solid var(--slate-200)' }}>
                                <table className="data-table">
                                <thead>
                                    <tr>
                                    <th>일시</th>
                                    <th>작성자</th>
                                    <th>내용</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white">
                                    <tr key={1}>
                                        <td className="text-slate-600">{counselData?.latest_record?.remark_date && moment(counselData.latest_record.remark_date).format('YYYY-MM-DD HH:mm:ss')}</td>
                                        <td className="text-slate-600">{counselData?.latest_record?.remark_reg_mem_name}</td>
                                        <td className="text-slate-800" style={{ whiteSpace: 'normal' }}>
                                            <div className="text-slate-500">{counselData?.latest_record?.remark}</div>
                                        </td>
                                    </tr>
                                </tbody>
                                </table>
                            </div>
                            </div>

                            {/* 2. Recent Counseling History */}
                            <div>
                            <h3 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
                                <span style={{ width: '0.375rem', height: '1.5rem', backgroundColor: 'var(--indigo-600)', borderRadius: '0.125rem' }}></span>
                                최근 상담 내역
                            </h3>
                            <div style={{ border: '1px solid var(--slate-200)', borderRadius: '0.5rem', overflow: 'hidden', backgroundColor: 'var(--white)', fontSize: '1rem' }}>
                                <div style={{ display: 'flex', borderBottom: '1px solid var(--slate-100)' }}>
                                <div style={{ width: '6rem', backgroundColor: 'var(--slate-50)', padding: '0.75rem', fontWeight: 700, color: 'var(--slate-600)', display: 'flex', alignItems: 'center', borderRight: '1px solid var(--slate-100)' }}>제목</div>
                                <div style={{ flex: 1, padding: '0.75rem', color: 'var(--slate-800)', fontWeight: 500 }}>
                                    {counselData?.latest_record?.counsel_title}
                                </div>
                                </div>
                                {/* Row 2: Date & Counselor */}
                                <div style={{ display: 'flex', borderBottom: '1px solid var(--slate-100)' }}>
                                <div style={{ width: '6rem', backgroundColor: 'var(--slate-50)', padding: '0.75rem', fontWeight: 700, color: 'var(--slate-600)', display: 'flex', alignItems: 'center', borderRight: '1px solid var(--slate-100)' }}>일시</div>
                                <div style={{ flex: 1, padding: '0.75rem', color: 'var(--slate-600)', borderRight: '1px solid var(--slate-100)' }}>
                                    {counselData?.latest_record?.counsel_date ? careFormatDate(counselData?.latest_record?.counsel_date) + ' ' + counselData?.latest_record?.counsel_time : '-'}
                                </div>
                                <div style={{ width: '6rem', backgroundColor: 'var(--slate-50)', padding: '0.75rem', fontWeight: 700, color: 'var(--slate-600)', display: 'flex', alignItems: 'center', borderRight: '1px solid var(--slate-100)' }}>상담자</div>
                                <div style={{ flex: 1, padding: '0.75rem', color: 'var(--slate-600)' }}>
                                    {counselData?.latest_record?.counsel_reg_mem_name}
                                </div>
                                </div>
                                {/* Row 3: Content */}
                                <div style={{ display: 'flex' }}>
                                <div style={{ width: '6rem', backgroundColor: 'var(--slate-50)', padding: '0.75rem', fontWeight: 700, color: 'var(--slate-600)', borderRight: '1px solid var(--slate-100)' }}>내용</div>
                                <div style={{ flex: 1, padding: '1rem', color: 'var(--slate-700)', lineHeight: 1.625, whiteSpace: 'pre-line' }}>
                                    {counselData?.latest_record?.counsel_content}
                                </div>
                                </div>
                            </div>
                            </div>
                        </div>
                    </Modal>

                    <Modal isOpen={activePopup.type === 'TEST_DETAIL'} onClose={closePopup} title="이전 성적" maxWidth="95%">
                        <div className="care-card-body" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '1rem' }}>
                        {semesterExamData.map((test, index) => (
                            <div key={index} className="care-card-base">
                            <div className="flex justify-between items-center care-mb-1">
                                <h4 className="font-bold text-slate-800 care-m-0">{!test.isPlacement ? test.semesterDisplayName : ''}</h4>
                                <span className="badge" style={{ fontSize: '1rem', lineHeight: '17.5px', fontWeight: 700, backgroundColor: test.isPlacement ? 'var(--amber-50)' : 'var(--indigo-50)', color: test.isPlacement ? '#b45309' : 'var(--indigo-600)' }}>
                                {test.courseName}
                                </span>
                            </div>
                            
                            {test.isPlacement ? (
                                <div className="text-center care-mt-1 care-flex-col-gap">
                                <div className="font-bold text-slate-600">{test.placementType}</div>
                                <div className="font-bold care-border-top">{test.placementResult + ' (' + test.courseName + ')'}</div>
                                </div>
                            ) : (
                                <table className="w-full test-history-table" >
                                <thead>
                                    <tr className="text-slate-400 care-text-sm">
                                    <th className="test-history-td">MT1</th>
                                    <th className="test-history-td">MT2</th>
                                    <th className="test-history-td">{MT3_LEVEL.some(level => test.courseName.toLowerCase().includes(level.toLowerCase())) ? 'MT3' : 'TT'}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="font-bold text-slate-600">
                                    <td className={'test-history-td ' + getScoreColorClass(test.scores?.MT1 || '-', test.assessLevel?.MT1 || '')}>{test.scores?.MT1}</td>
                                    <td className={'test-history-td ' + getScoreColorClass(test.scores?.MT2 || '-', test.assessLevel?.MT2 || '')}>{test.scores?.MT2}</td>
                                    <td className={'test-history-td ' + getScoreColorClass(MT3_LEVEL.some(level => test.courseName.toLowerCase().includes(level.toLowerCase())) ? (test.scores?.MT3 || '-') : (test.scores?.TT || '-'), MT3_LEVEL.some(level => test.courseName.toLowerCase().includes(level.toLowerCase())) ? (test.assessLevel?.MT3 || '') : (test.assessLevel?.TT || ''))}>
                                        {MT3_LEVEL.some(level => test.courseName.toLowerCase().includes(level.toLowerCase())) ? test.scores?.MT3 : test.scores?.TT}
                                    </td>
                                    </tr>
                                </tbody>
                                </table>
                            )}
                            </div>
                        ))}
                        </div>
                    </Modal>

                    {/* --- Portal eLearning Popup --- */}
                    {elearningState && (
                    <FixedPopover 
                        anchorEl={elearningState.anchor} 
                        onClose={() => setElearningState(null)}
                    >
                        <div className="popover-body">
                            <div className="care-popover-header">
                                <h4 className="popover-title">
                                    <AlertCircle size={14}/> 상세 내역
                                </h4>
                                <button onClick={() => setElearningState(null)} className="popover-close-btn">
                                    <X size={16} className="text-slate-400" />
                                </button>
                            </div>
                            <div className="popover-content-list">
                                {
                                    monthlyReadiData.find(data => data.id === elearningState?.idx)?.incompleteUnits?.map((unit, k) => (
                                        <div key={k} className="activity-item">
                                            <div className="activity-item-header">
                                                <span className={`activity-badge ${unit.type === '미수행' ? 'activity-badge-not-started' : 'activity-badge-low-score'}`}>
                                                    {unit.type}
                                                </span>
                                                <span className="font-semibold text-slate-700">{unit.unitName + ' ' + unit.title}</span>
                                            </div>
                                            <div className="activity-item-footer">
                                                <span className="care-text-xs">{careFormatDate(unit.date)}</span>
                                                {unit.score && <span className="text-red font-bold">{unit.score}점</span>}
                                            </div>
                                        </div>
                                    ))
                                }
                            </div>
                        </div>
                    </FixedPopover>
                    )}

                </main>
            </div>
        </>
    );
};

export default StudentCare;